import React, { useContext } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { useAgentContext } from '../hooks/useAgentContext';
import { CardSkeleton } from '../components/common/LoadingIndicators';
import {
  Button,
  Card,
  Badge,
  PlusIcon,
  HomeIcon,
  CpuChipIcon,
  CheckCircleIcon,
} from '../components/ui';
import {
  isCompletedStatus,
  isReviewStatus,
  isInProgressStatus,
  formatStatusForDisplay,
} from '../constants/agentProjectStatuses';

const MainPage: React.FC = () => {
  const authContext = useContext(AuthContext);
  const { cases, isLoadingCases, fetchUserCases } = useAgentContext();

  // Fetch user cases when component mounts
  React.useEffect(() => {
    fetchUserCases();
  }, [fetchUserCases]);

  // Calculate some quick stats using centralized status helpers
  const totalProjects = cases.length;
  const inProgressProjects = cases.filter((c) =>
    isInProgressStatus(c.status)
  ).length;
  const reviewProjects = cases.filter((c) => isReviewStatus(c.status)).length;
  const completedProjects = cases.filter((c) =>
    isCompletedStatus(c.status)
  ).length;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Welcome Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-secondary-900 mb-2">
          Welcome back,{' '}
          {authContext?.currentUser?.firstName ||
            authContext?.currentUser?.displayName?.split(' ')[0] ||
            authContext?.currentUser?.email?.split('@')[0]}
          !
        </h1>
        <p className="text-secondary-600">
          Design, architect, and plan AI Agents with expert guidance
        </p>
      </div>

      {/* Quick Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card>
          {isLoadingCases ? (
            <CardSkeleton rows={2} />
          ) : (
            <div>
              <div className="flex items-center mb-2">
                <CpuChipIcon className="w-5 h-5 text-primary-600 mr-2" />
                <h3 className="text-lg font-semibold text-secondary-900">
                  Total Projects
                </h3>
              </div>
              <p className="text-3xl font-bold text-primary-600">
                {totalProjects}
              </p>
            </div>
          )}
        </Card>

        <Card>
          {isLoadingCases ? (
            <CardSkeleton rows={2} />
          ) : (
            <div>
              <div className="flex items-center mb-2">
                <svg
                  className="w-5 h-5 text-warning-600 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
                <h3 className="text-lg font-semibold text-secondary-900">
                  In Progress
                </h3>
              </div>
              <p className="text-3xl font-bold text-warning-600">
                {inProgressProjects}
              </p>
            </div>
          )}
        </Card>

        <Card>
          {isLoadingCases ? (
            <CardSkeleton rows={2} />
          ) : (
            <div>
              <div className="flex items-center mb-2">
                <svg
                  className="w-5 h-5 text-info-600 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
                <h3 className="text-lg font-semibold text-secondary-900">
                  Under Review
                </h3>
              </div>
              <p className="text-3xl font-bold text-info-600">
                {reviewProjects}
              </p>
            </div>
          )}
        </Card>

        <Card>
          {isLoadingCases ? (
            <CardSkeleton rows={2} />
          ) : (
            <div>
              <div className="flex items-center mb-2">
                <CheckCircleIcon className="w-5 h-5 text-success-600 mr-2" />
                <h3 className="text-lg font-semibold text-secondary-900">
                  Completed
                </h3>
              </div>
              <p className="text-3xl font-bold text-success-600">
                {completedProjects}
              </p>
            </div>
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Quick Actions */}
        <Card variant="elevated">
          <h2 className="text-xl font-semibold text-secondary-900 mb-4">
            Quick Actions
          </h2>
          <div className="space-y-3">
            <RouterLink to="/projects/new" className="block">
              <Button
                variant="primary"
                size="lg"
                fullWidth
                leftIcon={<PlusIcon className="w-5 h-5" />}
              >
                Design New Agent
              </Button>
            </RouterLink>
            <RouterLink to="/projects" className="block">
              <Button
                variant="outline"
                size="lg"
                fullWidth
                leftIcon={<HomeIcon className="w-5 h-5" />}
              >
                View All Projects
              </Button>
            </RouterLink>
          </div>
        </Card>

        {/* System Features */}
        <Card variant="elevated">
          <h2 className="text-xl font-semibold text-secondary-900 mb-4">
            Platform Features
          </h2>
          <ul className="space-y-4">
            <li className="flex items-start gap-3">
              <CheckCircleIcon className="w-5 h-5 text-success-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-secondary-900">
                  AI-Powered Agent Design
                </p>
                <p className="text-sm text-secondary-600">
                  Interactive chat-based agent specification generation
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircleIcon className="w-5 h-5 text-success-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-secondary-900">
                  Technical Architecture
                </p>
                <p className="text-sm text-secondary-600">
                  Automated system design and infrastructure planning
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircleIcon className="w-5 h-5 text-success-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-secondary-900">
                  Google Cloud Integration
                </p>
                <p className="text-sm text-secondary-600">
                  Deploy to Vertex AI Agent Engine with best practices
                </p>
              </div>
            </li>
          </ul>
        </Card>
      </div>

      {/* Recent Projects Preview */}
      {cases.length > 0 && (
        <div className="mt-8">
          <h2 className="text-xl font-semibold text-secondary-900 mb-4">
            Recent Projects
          </h2>
          <Card>
            <ul className="divide-y divide-secondary-200">
              {cases.slice(0, 3).map((project) => (
                <li key={project.case_id}>
                  <RouterLink
                    to={`/projects/${project.case_id}`}
                    className="block py-4 px-2 hover:bg-secondary-50 transition-colors rounded"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-secondary-900">
                          {project.title}
                        </p>
                        <div className="flex items-center gap-3 mt-2">
                          <Badge
                            variant={
                              isCompletedStatus(project.status)
                                ? 'success'
                                : isReviewStatus(project.status)
                                  ? 'warning'
                                  : 'primary'
                            }
                          >
                            {formatStatusForDisplay(project.status)}
                          </Badge>
                          <span className="text-xs text-secondary-500">
                            Updated:{' '}
                            {new Date(project.updated_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </RouterLink>
                </li>
              ))}
            </ul>
            {cases.length > 3 && (
              <div className="text-center pt-4 border-t border-secondary-200">
                <RouterLink to="/projects">
                  <Button variant="ghost">
                    View All Projects ({cases.length})
                  </Button>
                </RouterLink>
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
};

export default MainPage;
